import React, { useState, useCallback } from 'react';
import {
  primeArtifactImportService,
  ImportPreview,
  ImportConflictResolution,
  PrimeArtifactImportRequest,
  ImportArtifactPreview,
} from '../../services/primeArtifactImportService';
import { PrimeArtifact } from '../../types/primeArtifact';
import { ImportTreeView } from './ImportTreeView';
import { IntentMLPrimeImportPanel } from './IntentMLPrimeImportPanel';
import styles from './PrimeArtifactImportDialog.module.css';

interface ArtifactHierarchyItem {
  id: string;
  name: string;
  description?: string;
  parent_id?: string;
  children: ArtifactHierarchyItem[];
  artifact_type_id?: string;
  is_root?: boolean;
}

interface PrimeArtifactImportDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onImportComplete: () => void;
  existingRootArtifacts: PrimeArtifact[];
}

type ImportStep =
  | 'file-selection'
  | 'preview'
  | 'hierarchy-validation'
  | 'conflict-resolution'
  | 'importing'
  | 'complete';

export const PrimeArtifactImportDialog: React.FC<PrimeArtifactImportDialogProps> = ({
  isOpen,
  onClose,
  onImportComplete,
  existingRootArtifacts,
}) => {
  const [currentStep, setCurrentStep] = useState<ImportStep>('file-selection');
  const [importMode, setImportMode] = useState<'legacy' | 'intentml'>('legacy');
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [targetRootId, setTargetRootId] = useState<string>('');
  const [importPreview, setImportPreview] = useState<ImportPreview | null>(null);
  const [conflictResolutions, setConflictResolutions] = useState<ImportConflictResolution[]>([]);
  const [jsonData, setJsonData] = useState<unknown[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [importResult, setImportResult] = useState<any>(null);
  const [selectedArtifact, setSelectedArtifact] = useState<ImportArtifactPreview | null>(null);
  const [artifactHierarchy, setArtifactHierarchy] = useState<ArtifactHierarchyItem[]>([]);

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file && file.type === 'application/json') {
      setSelectedFile(file);
      setError(null);
    } else {
      setError('Please select a valid JSON file');
    }
  };

  const handlePreviewImport = useCallback(async () => {
    if (!selectedFile) return;

    console.log('[handlePreviewImport] Starting preview import');
    setIsLoading(true);
    setError(null);

    try {
      // Read the file content for preview
      const fileContent = await selectedFile.text();
      const parsedData = JSON.parse(fileContent);
      console.log('[handlePreviewImport] Parsed data:', parsedData);
      setJsonData(parsedData);

      // Get preview from backend
      console.log(
        '[handlePreviewImport] targetRootId:',
        targetRootId,
        'resolved to:',
        targetRootId || undefined
      );
      const preview = await primeArtifactImportService.previewImport(
        selectedFile,
        targetRootId || undefined
      );

      setImportPreview(preview);

      // Initialize conflict resolutions
      const resolutions: ImportConflictResolution[] = [];
      preview.artifacts.forEach((artifact) => {
        if (artifact.conflict_type === 'duplicate') {
          resolutions.push({
            name: artifact.name,
            action: 'create_new',
            new_name: `${artifact.name} (Imported)`,
            parent_ref: targetRootId || 'root',
          });
        } else {
          resolutions.push({
            name: artifact.name,
            action: 'create_new',
            parent_ref: targetRootId || 'root',
          });
        }

        // Add resolutions for states, processes, and transitions
        artifact.states.forEach((state) => {
          if (state.conflict_type === 'duplicate') {
            resolutions.push({
              name: `${artifact.name}.${state.name}`,
              action: 'create_new',
              new_name: `${state.name} (Imported)`,
            });
          } else {
            resolutions.push({
              name: `${artifact.name}.${state.name}`,
              action: 'create_new',
            });
          }
        });

        artifact.processes.forEach((process) => {
          if (process.conflict_type === 'duplicate') {
            resolutions.push({
              name: `${artifact.name}.${process.name}`,
              action: 'create_new',
              new_name: `${process.name} (Imported)`,
            });
          } else {
            resolutions.push({
              name: `${artifact.name}.${process.name}`,
              action: 'create_new',
            });
          }
        });

        artifact.transitions.forEach((transition) => {
          resolutions.push({
            name: `${artifact.name}.${transition.name}`,
            action: 'create_new',
          });
        });
      });

      setConflictResolutions(resolutions);

      // Build hierarchy from the import data
      console.log('[handlePreviewImport] Building hierarchy from jsonData:', parsedData);
      const hierarchy = buildArtifactHierarchy(parsedData);
      console.log('[handlePreviewImport] Built hierarchy:', hierarchy);
      setArtifactHierarchy(hierarchy);

      setCurrentStep('preview');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to preview import');
    } finally {
      setIsLoading(false);
    }
  }, [selectedFile, targetRootId]);

  const handleConflictResolutionChange = (name: string, resolution: ImportConflictResolution) => {
    setConflictResolutions((prev) => prev.map((r) => (r.name === name ? resolution : r)));
  };

  const buildArtifactHierarchy = (importData: unknown): ArtifactHierarchyItem[] => {
    console.log('[buildArtifactHierarchy] Input data:', importData);
    const hierarchyMap = new Map<string, ArtifactHierarchyItem>();
    const rootItems: ArtifactHierarchyItem[] = [];

    // Handle both object format (with prime_artifacts key) and array format
    let artifacts: unknown[] = [];
    if (Array.isArray(importData)) {
      // Array format - each item has a prime_artifact property
      artifacts = importData;
      console.log(
        '[buildArtifactHierarchy] Using array format, artifacts count:',
        artifacts.length
      );
    } else if (importData && typeof importData === 'object') {
      const data = importData as Record<string, unknown>;
      // Object format - extract prime_artifacts array
      if (data.prime_artifacts && Array.isArray(data.prime_artifacts)) {
        artifacts = data.prime_artifacts;
        console.log(
          '[buildArtifactHierarchy] Using object format, artifacts count:',
          artifacts.length
        );
      } else {
        console.log(
          '[buildArtifactHierarchy] No prime_artifacts array found in object:',
          Object.keys(data)
        );
      }
    }

    // First pass: create all hierarchy items
    console.log('[buildArtifactHierarchy] Processing artifacts:', artifacts.length);
    artifacts.forEach((artifactData, index) => {
      let artifact: Record<string, unknown>;

      if (Array.isArray(importData)) {
        // Array format
        const data = artifactData as Record<string, unknown>;
        artifact = data.prime_artifact as Record<string, unknown>;
        if (!artifact) {
          console.log('[buildArtifactHierarchy] Skipping item', index, 'no prime_artifact');
          return;
        }
      } else {
        // Object format - artifactData is already the artifact
        artifact = artifactData as Record<string, unknown>;
      }

      console.log(
        '[buildArtifactHierarchy] Processing artifact:',
        artifact.name,
        'parent_ref:',
        artifact.parent_ref
      );

      const hierarchyItem: ArtifactHierarchyItem = {
        id: (artifact.id as string) || `temp-${Math.random()}`,
        name: artifact.name as string,
        description: artifact.description as string,
        parent_id: artifact.parent_ref as string,
        children: [],
        artifact_type_id: artifact.artifact_type_id as string,
        is_root: !artifact.parent_ref,
      };

      hierarchyMap.set(hierarchyItem.id, hierarchyItem);
    });

    // Second pass: build parent-child relationships
    console.log('[buildArtifactHierarchy] Building relationships for', hierarchyMap.size, 'items');
    hierarchyMap.forEach((item) => {
      if (item.is_root || !item.parent_id) {
        console.log('[buildArtifactHierarchy] Adding root item:', item.name);
        rootItems.push(item);
      } else {
        const parent = hierarchyMap.get(item.parent_id);
        if (parent) {
          console.log('[buildArtifactHierarchy] Adding child', item.name, 'to parent', parent.name);
          parent.children.push(item);
        } else {
          // If parent not found, treat as root
          console.log(
            '[buildArtifactHierarchy] Parent not found for',
            item.name,
            ', treating as root'
          );
          rootItems.push(item);
        }
      }
    });

    console.log('[buildArtifactHierarchy] Final root items:', rootItems.length);
    return rootItems;
  };

  const handleExecuteImport = useCallback(async () => {
    // Check if jsonData exists and has content (it could be an object or array)
    const hasJsonData =
      jsonData &&
      (Array.isArray(jsonData)
        ? jsonData.length > 0
        : typeof jsonData === 'object'
          ? Object.keys(jsonData).length > 0
          : false);

    if (!hasJsonData || !conflictResolutions.length) {
      return;
    }

    setIsLoading(true);
    setError(null);
    setCurrentStep('importing');

    try {
      const request: PrimeArtifactImportRequest = {
        json_data: jsonData,
        conflict_resolutions: conflictResolutions,
        target_root_id: targetRootId || undefined,
      };

      const result = await primeArtifactImportService.executeImport(request);
      setImportResult(result);
      setCurrentStep('complete');
    } catch (err) {
      console.error('Import error:', err);
      setError(err instanceof Error ? err.message : 'Failed to execute import');
      setCurrentStep('conflict-resolution');
    } finally {
      setIsLoading(false);
    }
  }, [jsonData, conflictResolutions, targetRootId]);

  const handleClose = () => {
    setCurrentStep('file-selection');
    setImportMode('legacy');
    setSelectedFile(null);
    setTargetRootId('');
    setImportPreview(null);
    setConflictResolutions([]);
    setJsonData([]);
    setError(null);
    setImportResult(null);
    onClose();
  };

  const handleImportComplete = () => {
    handleClose();
    onImportComplete();
  };

  if (!isOpen) return null;

  return (
    <div className={styles.importDialogOverlay}>
      <div className={styles.importDialog}>
        <div className={styles.importDialogHeader}>
          <h2>Import Prime Artifacts</h2>
          <button className={styles.closeButton} onClick={handleClose}>
            ×
          </button>
        </div>

        <div className={styles.importDialogContent}>
          {currentStep === 'file-selection' && (
            <>
              <div className={styles.importModeBar}>
                <button
                  type="button"
                  className={importMode === 'legacy' ? styles.activeMode : undefined}
                  onClick={() => setImportMode('legacy')}
                >
                  Standard JSON import
                </button>
                <button
                  type="button"
                  className={importMode === 'intentml' ? styles.activeMode : undefined}
                  onClick={() => setImportMode('intentml')}
                >
                  IntentML prime_model
                </button>
              </div>
              {importMode === 'intentml' ? (
                <IntentMLPrimeImportPanel onImportComplete={handleImportComplete} />
              ) : (
                <FileSelectionStep
                  onFileSelect={handleFileSelect}
                  onTargetRootChange={setTargetRootId}
                  targetRootId={targetRootId}
                  existingRootArtifacts={existingRootArtifacts}
                  onNext={handlePreviewImport}
                  isLoading={isLoading}
                  error={error}
                />
              )}
            </>
          )}

          {currentStep === 'preview' && importPreview && (
            <PreviewStep
              preview={importPreview}
              selectedArtifact={selectedArtifact}
              onArtifactSelect={setSelectedArtifact}
              onNext={() => setCurrentStep('hierarchy-validation')}
              onBack={() => setCurrentStep('file-selection')}
            />
          )}

          {currentStep === 'hierarchy-validation' && artifactHierarchy.length > 0 && (
            <HierarchyValidationStep
              hierarchy={artifactHierarchy}
              onHierarchyChange={setArtifactHierarchy}
              onNext={() => setCurrentStep('conflict-resolution')}
              onBack={() => setCurrentStep('preview')}
            />
          )}

          {currentStep === 'hierarchy-validation' && artifactHierarchy.length === 0 && (
            <div className={styles.importStep}>
              <h3>Loading Hierarchy...</h3>
              <p>Please wait while we process the artifact hierarchy.</p>
              <div className={styles.loadingSpinner}>
                <div className={styles.spinner}></div>
              </div>
            </div>
          )}

          {currentStep === 'conflict-resolution' && importPreview && (
            <ConflictResolutionStep
              preview={importPreview}
              conflictResolutions={conflictResolutions}
              onResolutionChange={handleConflictResolutionChange}
              onExecute={handleExecuteImport}
              onBack={() => setCurrentStep('preview')}
              isLoading={isLoading}
              error={error}
            />
          )}

          {currentStep === 'importing' && <ImportingStep />}

          {currentStep === 'complete' && importResult && (
            <CompleteStep result={importResult} onClose={handleImportComplete} />
          )}
        </div>
      </div>
    </div>
  );
};

// File Selection Step Component
interface FileSelectionStepProps {
  onFileSelect: (event: React.ChangeEvent<HTMLInputElement>) => void;
  onTargetRootChange: (rootId: string) => void;
  targetRootId: string;
  existingRootArtifacts: PrimeArtifact[];
  onNext: () => void;
  isLoading: boolean;
  error: string | null;
}

const FileSelectionStep: React.FC<FileSelectionStepProps> = ({
  onFileSelect,
  onTargetRootChange,
  targetRootId,
  existingRootArtifacts,
  onNext,
  isLoading,
  error,
}) => {
  return (
    <div className={styles.importStep}>
      <h3>Select JSON File and Target Root</h3>

      <div className={styles.formGroup}>
        <label htmlFor="json-file">JSON File:</label>
        <input
          id="json-file"
          type="file"
          accept=".json"
          onChange={onFileSelect}
          disabled={isLoading}
        />
        <small>Select a JSON file containing prime artifacts to import</small>
      </div>

      <div className={styles.formGroup}>
        <label htmlFor="target-root">Target Root Artifact:</label>
        <select
          id="target-root"
          value={targetRootId}
          onChange={(e) => onTargetRootChange(e.target.value)}
          disabled={isLoading}
        >
          <option value="">Create New Root</option>
          {existingRootArtifacts.map((artifact) => (
            <option key={artifact.id} value={artifact.id}>
              {artifact.name}
            </option>
          ))}
        </select>
        <small>Select an existing root artifact or create a new one</small>
      </div>

      {error && <div className={styles.errorMessage}>{error}</div>}

      <div className={styles.stepActions}>
        <button onClick={onNext} disabled={isLoading} className={styles.primaryButton}>
          {isLoading ? 'Previewing...' : 'Preview Import'}
        </button>
      </div>
    </div>
  );
};

// Preview Step Component
interface PreviewStepProps {
  preview: ImportPreview;
  selectedArtifact: ImportArtifactPreview | null;
  onArtifactSelect: (artifact: ImportArtifactPreview) => void;
  onNext: () => void;
  onBack: () => void;
}

const PreviewStep: React.FC<PreviewStepProps> = ({
  preview,
  selectedArtifact,
  onArtifactSelect,
  onNext,
  onBack,
}) => {
  return (
    <div className={styles.importStep}>
      <h3>Import Preview</h3>

      <div className={styles.previewSummary}>
        <div className={styles.summaryItem}>
          <span className={styles.label}>Total Artifacts:</span>
          <span className={styles.value}>{preview.total_artifacts}</span>
        </div>
        <div className={styles.summaryItem}>
          <span className={styles.label}>New Artifacts:</span>
          <span className={`${styles.value} ${styles.new}`}>{preview.new_artifacts}</span>
        </div>
        <div className={styles.summaryItem}>
          <span className={styles.label}>Conflicts:</span>
          <span className={`${styles.value} ${styles.conflict}`}>{preview.conflicts}</span>
        </div>
      </div>

      <div className={styles.previewContent}>
        <div className={styles.previewTree}>
          <ImportTreeView
            artifacts={preview.artifacts}
            onArtifactSelect={onArtifactSelect}
            selectedArtifact={selectedArtifact}
          />
        </div>

        {selectedArtifact && (
          <div className={styles.artifactDetailsPanel}>
            <h4>Selected Artifact Details</h4>
            <div className={styles.artifactDetailContent}>
              <div className={styles.detailSection}>
                <h5>Basic Information</h5>
                <p>
                  <strong>Name:</strong> {selectedArtifact.name}
                </p>
                {selectedArtifact.description && (
                  <p>
                    <strong>Description:</strong> {selectedArtifact.description}
                  </p>
                )}
                <p>
                  <strong>Type:</strong>{' '}
                  <span className={`${styles.conflictBadge} ${styles[selectedArtifact.conflict_type]}`}>
                    {selectedArtifact.conflict_type}
                  </span>
                </p>
              </div>

              <div className={styles.detailSection}>
                <h5>States ({selectedArtifact.states.length})</h5>
                {selectedArtifact.states.length > 0 ? (
                  <ul>
                    {selectedArtifact.states.map((state, index) => (
                      <li key={index} className={styles.stateItem}>
                        <span className={styles.stateName}>{state.name}</span>
                        <span className={`${styles.conflictBadge} ${styles[state.conflict_type]}`}>
                          {state.conflict_type}
                        </span>
                        {state.is_initial && <span className={`${styles.flag} ${styles.initial}`}>Initial</span>}
                        {state.is_final && <span className={`${styles.flag} ${styles.final}`}>Final</span>}
                      </li>
                    ))}
                  </ul>
                ) : (
                  <p className={styles.noItems}>No states defined</p>
                )}
              </div>

              <div className={styles.detailSection}>
                <h5>Processes ({selectedArtifact.processes.length})</h5>
                {selectedArtifact.processes.length > 0 ? (
                  <ul>
                    {selectedArtifact.processes.map((process, index) => (
                      <li key={index} className={styles.processItem}>
                        <span className={styles.processName}>{process.name}</span>
                        <span className={`${styles.conflictBadge} ${styles[process.conflict_type]}`}>
                          {process.conflict_type}
                        </span>
                        {process.process_type && (
                          <span className={styles.processType}>{process.process_type}</span>
                        )}
                      </li>
                    ))}
                  </ul>
                ) : (
                  <p className="no-items">No processes defined</p>
                )}
              </div>

              <div className={styles.detailSection}>
                <h5>Transitions ({selectedArtifact.transitions.length})</h5>
                {selectedArtifact.transitions.length > 0 ? (
                  <ul>
                    {selectedArtifact.transitions.map((transition, index) => (
                      <li key={index} className={styles.transitionItem}>
                        <span className={styles.transitionName}>{transition.name}</span>
                        <span className={`${styles.conflictBadge} ${styles[transition.conflict_type]}`}>
                          {transition.conflict_type}
                        </span>
                        {transition.from_state && transition.to_state && (
                          <span className={styles.transitionFlow}>
                            {transition.from_state} → {transition.to_state}
                          </span>
                        )}
                      </li>
                    ))}
                  </ul>
                ) : (
                  <p className="no-items">No transitions defined</p>
                )}
              </div>
            </div>
          </div>
        )}
      </div>

      <div className={styles.stepActions}>
        <button onClick={onBack} className={styles.secondaryButton}>
          Back
        </button>
        <button onClick={onNext} className={styles.primaryButton}>
          Resolve Conflicts
        </button>
      </div>
    </div>
  );
};

// Conflict Resolution Step Component
interface ConflictResolutionStepProps {
  preview: ImportPreview;
  conflictResolutions: ImportConflictResolution[];
  onResolutionChange: (name: string, resolution: ImportConflictResolution) => void;
  onExecute: () => void;
  onBack: () => void;
  isLoading: boolean;
  error: string | null;
}

const ConflictResolutionStep: React.FC<ConflictResolutionStepProps> = ({
  preview,
  conflictResolutions,
  onResolutionChange,
  onExecute,
  onBack,
  isLoading,
  error,
}) => {
  const artifactResolutions = conflictResolutions.filter(
    (r) => !r.name.includes('.') && preview.artifacts.some((a) => a.name === r.name)
  );

  return (
    <div className={styles.importStep}>
      <h3>Resolve Conflicts</h3>

      <div className={styles.conflictResolutions}>
        {artifactResolutions.map((resolution, index) => (
          <div key={`${resolution.name}-${index}`} className={styles.conflictResolution}>
            <div className={styles.conflictHeader}>
              <span className={styles.itemName}>{resolution.name}</span>
              <span className={styles.conflictType}>duplicate</span>
            </div>

            <div className={styles.resolutionOptions}>
              <label>
                <input
                  type="radio"
                  name={`action-${resolution.name}`}
                  value="create_new"
                  checked={resolution.action === 'create_new'}
                  onChange={(e) =>
                    onResolutionChange(resolution.name, {
                      ...resolution,
                      action: e.target.value as any,
                    })
                  }
                />
                Create New
              </label>

              <label>
                <input
                  type="radio"
                  name={`action-${resolution.name}`}
                  value="use_existing"
                  checked={resolution.action === 'use_existing'}
                  onChange={(e) =>
                    onResolutionChange(resolution.name, {
                      ...resolution,
                      action: e.target.value as any,
                    })
                  }
                />
                Use Existing
              </label>

              <label>
                <input
                  type="radio"
                  name={`action-${resolution.name}`}
                  value="skip"
                  checked={resolution.action === 'skip'}
                  onChange={(e) =>
                    onResolutionChange(resolution.name, {
                      ...resolution,
                      action: e.target.value as any,
                    })
                  }
                />
                Skip
              </label>
            </div>

            {resolution.action === 'create_new' && (
              <div className={styles.newNameInput}>
                <label>New Name:</label>
                <input
                  type="text"
                  value={resolution.new_name || ''}
                  onChange={(e) =>
                    onResolutionChange(resolution.name, {
                      ...resolution,
                      new_name: e.target.value,
                    })
                  }
                  placeholder={`${resolution.name} (Imported)`}
                />
              </div>
            )}
          </div>
        ))}
      </div>

      {error && <div className={styles.errorMessage}>{error}</div>}

      <div className={styles.stepActions}>
        <button onClick={onBack} className={styles.secondaryButton}>
          Back
        </button>
        <button onClick={onExecute} disabled={isLoading} className={styles.primaryButton}>
          {isLoading ? 'Importing...' : 'Execute Import'}
        </button>
      </div>
    </div>
  );
};

// Hierarchy Validation Step Component
interface HierarchyValidationStepProps {
  hierarchy: ArtifactHierarchyItem[];
  onHierarchyChange: (hierarchy: ArtifactHierarchyItem[]) => void;
  onNext: () => void;
  onBack: () => void;
}

const HierarchyValidationStep: React.FC<HierarchyValidationStepProps> = ({
  hierarchy,
  onHierarchyChange,
  onNext,
  onBack,
}) => {
  const [selectedItem, setSelectedItem] = useState<ArtifactHierarchyItem | null>(null);
  const [draggedItem, setDraggedItem] = useState<ArtifactHierarchyItem | null>(null);

  const handleDragStart = (item: ArtifactHierarchyItem) => {
    setDraggedItem(item);
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
  };

  const handleDrop = (targetItem: ArtifactHierarchyItem) => {
    if (!draggedItem || draggedItem.id === targetItem.id) {
      setDraggedItem(null);
      return;
    }

    // Update hierarchy
    const newHierarchy = updateHierarchy(hierarchy, draggedItem, targetItem);
    onHierarchyChange(newHierarchy);
    setDraggedItem(null);
  };

  const handleRootSelection = (rootId: string) => {
    // Update hierarchy to set the selected root and remove root status from others
    const updateRootStatus = (items: ArtifactHierarchyItem[]): ArtifactHierarchyItem[] => {
      return items.map((item) => ({
        ...item,
        is_root: item.id === rootId,
        parent_id: item.id === rootId ? undefined : item.parent_id,
        children: updateRootStatus(item.children),
      }));
    };

    const newHierarchy = updateRootStatus(hierarchy);
    onHierarchyChange(newHierarchy);
  };

  const updateHierarchy = (
    hierarchy: ArtifactHierarchyItem[],
    draggedItem: ArtifactHierarchyItem,
    targetItem: ArtifactHierarchyItem
  ): ArtifactHierarchyItem[] => {
    // Remove dragged item from its current position
    const removeFromParent = (items: ArtifactHierarchyItem[]): ArtifactHierarchyItem[] => {
      return items.map((item) => ({
        ...item,
        children: item.children
          .filter((child) => child.id !== draggedItem.id)
          .map((child) => ({
            ...child,
            children: removeFromParent(child.children),
          })),
      }));
    };

    // Add dragged item to target
    const addToTarget = (items: ArtifactHierarchyItem[]): ArtifactHierarchyItem[] => {
      return items.map((item) => {
        if (item.id === targetItem.id) {
          return {
            ...item,
            children: [...item.children, { ...draggedItem, parent_id: item.id }],
          };
        }
        return {
          ...item,
          children: addToTarget(item.children),
        };
      });
    };

    let updated = removeFromParent(hierarchy);
    updated = addToTarget(updated);
    return updated;
  };

  const renderHierarchyItem = (item: ArtifactHierarchyItem, level: number = 0) => (
    <div
      key={item.id}
      className={`${styles.hierarchyItem} ${styles[`level-${level}`] || ''} ${selectedItem?.id === item.id ? styles.selected : ''}`}
      style={{ marginLeft: `${level * 20}px` }}
      draggable
      onDragStart={() => handleDragStart(item)}
      onDragOver={handleDragOver}
      onDrop={() => handleDrop(item)}
      onClick={() => setSelectedItem(item)}
    >
      <div className={styles.itemHeader}>
        <span className={styles.itemName}>{item.name}</span>
        {item.is_root && <span className={styles.rootBadge}>ROOT</span>}
      </div>
      {item.description && <div className={styles.itemDescription}>{item.description}</div>}
      <div className={styles.itemType}>{item.artifact_type_id}</div>

      {item.children.map((child) => renderHierarchyItem(child, level + 1))}
    </div>
  );

  const validateHierarchy = (): { isValid: boolean; errors: string[] } => {
    const errors: string[] = [];

    // Check for multiple roots
    const rootCount = hierarchy.filter((item) => item.is_root).length;
    if (rootCount === 0) {
      errors.push('No root artifact found. Please select a root artifact below.');
    } else if (rootCount > 1) {
      errors.push(
        `Multiple root artifacts found (${rootCount}). Please select only one root artifact below.`
      );
    }

    // Check for orphaned items
    const allItems = getAllItems(hierarchy);
    const orphanedItems = allItems.filter((item) => !item.is_root && !item.parent_id);
    if (orphanedItems.length > 0) {
      errors.push(
        `Found ${orphanedItems.length} orphaned artifacts without parent: ${orphanedItems.map((item) => item.name).join(', ')}`
      );
    }

    return {
      isValid: errors.length === 0,
      errors,
    };
  };

  const getAllItems = (items: ArtifactHierarchyItem[]): ArtifactHierarchyItem[] => {
    let result: ArtifactHierarchyItem[] = [];
    items.forEach((item) => {
      result.push(item);
      result = result.concat(getAllItems(item.children));
    });
    return result;
  };

  const validation = validateHierarchy();
  const allItems = getAllItems(hierarchy);

  console.log('[HierarchyValidationStep] hierarchy:', hierarchy);
  console.log('[HierarchyValidationStep] allItems:', allItems);
  console.log('[HierarchyValidationStep] validation:', validation);

  return (
    <div className={styles.importStep}>
      <h3>Validate Artifact Hierarchy</h3>
      <p>
        Review and adjust the artifact hierarchy. All artifacts must be connected to a single root.
      </p>

      {/* Root Selection */}
      {validation.errors.some((error) => error.includes('root')) && (
        <div className={styles.rootSelection}>
          <h4>Select Root Artifact</h4>
          <p>Choose which artifact should be the root of your hierarchy:</p>
          <div className={styles.rootOptions}>
            {allItems.map((item) => (
              <label key={item.id} className={styles.rootOption}>
                <input
                  type="radio"
                  name="root-selection"
                  value={item.id}
                  checked={item.is_root}
                  onChange={() => handleRootSelection(item.id)}
                />
                <span className={styles.optionName}>{item.name}</span>
                <span className={styles.optionType}>({item.artifact_type_id})</span>
              </label>
            ))}
          </div>
        </div>
      )}

      <div className={styles.hierarchyContainer}>
        <div className={styles.hierarchyTree}>
          {hierarchy.length === 0 ? (
            <div className={styles.noItems}>No artifacts found in hierarchy</div>
          ) : (
            hierarchy.map((item) => renderHierarchyItem(item))
          )}
        </div>

        {selectedItem && (
          <div className={styles.hierarchyDetails}>
            <h4>Selected Item Details</h4>
            <div className={styles.detailField}>
              <label>Name:</label>
              <span>{selectedItem.name}</span>
            </div>
            <div className={styles.detailField}>
              <label>Type:</label>
              <span>{selectedItem.artifact_type_id}</span>
            </div>
            <div className={styles.detailField}>
              <label>Parent:</label>
              <span>{selectedItem.parent_id || 'None (Root)'}</span>
            </div>
            {selectedItem.description && (
              <div className={styles.detailField}>
                <label>Description:</label>
                <span>{selectedItem.description}</span>
              </div>
            )}
          </div>
        )}
      </div>

      {!validation.isValid && (
        <div className={styles.validationErrors}>
          <h4>Validation Errors:</h4>
          <ul>
            {validation.errors.map((error, index) => (
              <li key={index} className={styles.errorItem}>
                {error}
              </li>
            ))}
          </ul>
        </div>
      )}

      <div className={styles.stepActions}>
        <button onClick={onBack} className={styles.secondaryButton}>
          Back
        </button>
        <button onClick={onNext} disabled={!validation.isValid} className={styles.primaryButton}>
          Continue to Conflicts
        </button>
      </div>
    </div>
  );
};

// Importing Step Component
const ImportingStep: React.FC = () => {
  return (
    <div className={styles.importStep}>
      <h3>Importing...</h3>
      <div className={styles.importingSpinner}>
        <div className={styles.spinner}></div>
        <p>Please wait while we import your prime artifacts...</p>
      </div>
    </div>
  );
};

// Complete Step Component
interface CompleteStepProps {
  result: any;
  onClose: () => void;
}

const CompleteStep: React.FC<CompleteStepProps> = ({ result, onClose }) => {
  return (
    <div className={styles.importStep}>
      <h3>Import Complete!</h3>

      <div className={styles.importResults}>
        <div className={styles.resultItem}>
          <span className={styles.label}>Artifacts Created:</span>
          <span className={styles.value}>{result.created_artifacts}</span>
        </div>
        <div className={styles.resultItem}>
          <span className={styles.label}>States Created:</span>
          <span className={styles.value}>{result.created_states}</span>
        </div>
        <div className={styles.resultItem}>
          <span className={styles.label}>Processes Created:</span>
          <span className={styles.value}>{result.created_processes}</span>
        </div>
        <div className={styles.resultItem}>
          <span className={styles.label}>Transitions Created:</span>
          <span className={styles.value}>{result.created_transitions}</span>
        </div>
      </div>

      <div className={styles.stepActions}>
        <button onClick={onClose} className={styles.primaryButton}>
          Close
        </button>
      </div>
    </div>
  );
};
